import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  type ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart"
import { cn } from "@/lib/utils"
import type { MissionAnalyticsTrendPoint } from "@/types/mission-analytics"

/**
 * OKS-63 — Completion-trend line chart for the cross-mission overview.
 *
 * Structural copy of `InviteActivityChart` (OKS-21) with 2 series
 * instead of 4 (completions + approvals). Same a11y posture:
 *   - chart container `role="img"` + summary `aria-label`
 *   - sibling `<table class="sr-only">` mirror — one row per day,
 *     columns: date / completions / approvals.
 *
 * Empty-state behaviour: when `trend.length === 0` OR every datapoint
 * is flat-zero, the body renders the empty card variant. The card
 * surface itself does NOT collapse (layout stability).
 *
 * Color tokens come from `--chart-1` / `--chart-2` (shadcn chart
 * system). No hardcoded hex; `--primary` is reserved for brand
 * expression and not used in data viz.
 */
export interface AnalyticsTrendChartProps {
  trend: MissionAnalyticsTrendPoint[]
  className?: string
}

export function AnalyticsTrendChart({
  trend,
  className,
}: AnalyticsTrendChartProps) {
  const { t } = useTranslation()

  const config: ChartConfig = useMemo(
    () => ({
      completions: {
        label: t("analytics.trend.series.completions"),
        theme: { light: "var(--chart-1)", dark: "var(--chart-1)" },
      },
      approvals: {
        label: t("analytics.trend.series.approvals"),
        theme: { light: "var(--chart-2)", dark: "var(--chart-2)" },
      },
    }),
    [t],
  )

  const isEmpty =
    trend.length === 0 ||
    trend.every((row) => row.completions === 0 && row.approvals === 0)

  return (
    <Card
      data-testid="analytics-overview-trend-chart"
      className={cn("flex flex-col gap-3 p-4 sm:p-6", className)}
    >
      <CardHeader className="p-0">
        <CardTitle className="text-foreground text-base font-semibold">
          {t("analytics.trend.title")}
        </CardTitle>
      </CardHeader>

      <CardContent className="p-0">
        {isEmpty ? <EmptyState /> : <ChartBody config={config} trend={trend} />}
        <SrOnlyTable trend={trend} />
      </CardContent>
    </Card>
  )
}

interface ChartBodyProps {
  config: ChartConfig
  trend: MissionAnalyticsTrendPoint[]
}

function ChartBody({ config, trend }: ChartBodyProps) {
  const { t } = useTranslation()
  const totalCompletions = trend.reduce((acc, r) => acc + r.completions, 0)
  const totalApprovals = trend.reduce((acc, r) => acc + r.approvals, 0)
  const ariaSummary = t("analytics.trend.aria_summary", {
    start: trend[0]?.date,
    end_date: trend[trend.length - 1]?.date,
    completions: totalCompletions,
    approvals: totalApprovals,
  })

  return (
    <div
      data-testid="analytics-overview-trend-chart-canvas"
      role="img"
      aria-label={ariaSummary}
    >
      <ChartContainer
        config={config}
        className="aspect-auto h-[200px] w-full sm:h-[240px] lg:h-[260px]"
      >
        <LineChart
          data={trend}
          margin={{ left: 4, right: 12, top: 8, bottom: 0 }}
        >
          <CartesianGrid vertical={false} strokeDasharray="3 3" />
          <XAxis
            dataKey="date"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            minTickGap={24}
            tickFormatter={(value: string) =>
              new Date(value).toLocaleDateString(undefined, {
                month: "short",
                day: "numeric",
              })
            }
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            allowDecimals={false}
            domain={[0, "auto"]}
            width={32}
          />
          <ChartTooltip content={<ChartTooltipContent />} />
          <ChartLegend
            data-testid="analytics-overview-trend-chart-legend"
            content={<ChartLegendContent />}
          />
          {(["completions", "approvals"] as const).map((key) => (
            <Line
              key={key}
              type="monotone"
              dataKey={key}
              stroke={`var(--color-${key})`}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ChartContainer>
    </div>
  )
}

function EmptyState() {
  const { t } = useTranslation()
  return (
    <div
      data-testid="analytics-overview-trend-chart-empty"
      className="text-muted-foreground flex h-[200px] flex-col items-center justify-center gap-2 text-sm sm:h-[240px] lg:h-[260px]"
    >
      <p className="text-foreground text-sm font-medium">
        {t("analytics.trend.empty.title")}
      </p>
      <p className="max-w-md text-center">{t("analytics.trend.empty.body")}</p>
    </div>
  )
}

/**
 * Sr-only mirror table — one row per day. Screen readers consume this
 * directly; visual users see the chart. Mirrors the
 * `InviteActivityChart` (OKS-21) posture; also serves as a stable
 * Playwright selector (canvas testing is fragile).
 */
function SrOnlyTable({ trend }: { trend: MissionAnalyticsTrendPoint[] }) {
  const { t } = useTranslation()
  return (
    <table
      className="sr-only"
      data-testid="analytics-overview-trend-chart-mirror-table"
    >
      <caption>{t("analytics.trend.mirror_table_caption")}</caption>
      <thead>
        <tr>
          <th>{t("analytics.trend.mirror_table.date")}</th>
          <th>{t("analytics.trend.mirror_table.completions")}</th>
          <th>{t("analytics.trend.mirror_table.approvals")}</th>
        </tr>
      </thead>
      <tbody>
        {trend.map((row) => (
          <tr key={row.date}>
            <td>{row.date}</td>
            <td>{row.completions}</td>
            <td>{row.approvals}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
